import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import ModalCobro from '../components/ModalCobro';
import Ticket from '../components/Ticket';

export default function TableOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [activeView, setActiveView] = useState('products');
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adding, setAdding] = useState({});
  const addingRef = useRef({});
  const sendingKitchenRef = useRef(false);
  const reprintingRef = useRef(false);
  const [activeCategory, setActiveCategory] = useState('todas');
  const [showBillTypeModal, setShowBillTypeModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsAlreadyInvoiced, setItemsAlreadyInvoiced] = useState(
    location.state?.items_ya_facturados || []
  );
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [itemsForCharge, setItemsForCharge] = useState([]);
  const [chargeTotal, setChargeTotal] = useState(0);
  const [saleResult, setSaleResult] = useState(null);
  const [isSplit, setIsSplit] = useState(false);
  const [showChangeTableModal, setShowChangeTableModal] = useState(false);
  const [availableTables, setAvailableTables] = useState([]);
  const [changingTable, setChangingTable] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadOrder, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.state?.abrir_separada && !loading) {
      setSelectedItems([]);
      setShowSplitModal(true);
    }
  }, [loading]);

  const loadOrder = async () => {
    try {
      const res = await api.get(`/ordenes/${id}`);
      setOrder(res.data);
    } catch (error) {
      console.error('Error reloading order:', error);
    }
  };

  const loadData = async () => {
    try {
      const [orderRes, productsRes] = await Promise.all([
        api.get(`/ordenes/${id}`),
        api.get('/restaurante/productos'),
      ]);
      setOrder(orderRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      Swal.fire('Error', 'Could not load the order', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (product) => {
    if (addingRef.current[product.id]) return;
    addingRef.current[product.id] = true;
    setAdding((prev) => ({ ...prev, [product.id]: true }));

    const tempItem = {
      id: `temp-${Date.now()}`,
      product_id: product.id,
      producto: product,
      cantidad: 1,
      precio_unitario: product.precio_venta,
      subtotal: product.precio_venta,
      enviado_cocina: false,
      listo: false,
    };
    setOrder((prev) => ({
      ...prev,
      items: [...(prev.items || []), tempItem],
      total: (parseFloat(prev.total) + parseFloat(product.precio_venta)).toFixed(2),
      subtotal: (parseFloat(prev.subtotal) + parseFloat(product.precio_venta)).toFixed(2),
    }));

    try {
      await api.post(`/ordenes/${id}/items`, {
        items: [{ product_id: product.id, cantidad: 1 }],
      });
      loadOrder();
    } catch (error) {
      loadOrder();
      Swal.fire('Error', 'Could not add the product', 'error');
    } finally {
      addingRef.current[product.id] = false;
      setAdding((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const removeItem = async (itemId) => {
    const itemToRemove = order?.items?.find((i) => i.id === itemId);
    if (!itemToRemove) return;

    setOrder((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
      total: Math.max(0, parseFloat(prev.total) - parseFloat(itemToRemove.subtotal)).toFixed(2),
      subtotal: Math.max(0, parseFloat(prev.subtotal) - parseFloat(itemToRemove.subtotal)).toFixed(2),
    }));

    try {
      await api.delete(`/ordenes/items/${itemId}`);
      loadOrder();
    } catch (error) {
      loadOrder();
      Swal.fire('Error', error.response?.data?.message || 'Could not remove the item', 'error');
    }
  };

  const sendToKitchen = async () => {
    if (sendingKitchenRef.current) return;
    sendingKitchenRef.current = true;

    try {
      // Refresh the order from the server first to make sure we use
      // real IDs instead of temp IDs (avoids empty kitchen tickets)
      const freshOrder = await api.get(`/ordenes/${id}`);
      const pendingItems = freshOrder.data.items.filter((item) => !item.enviado_cocina);

      if (pendingItems.length === 0) {
        Swal.fire('Notice', 'No items pending to send to the kitchen', 'warning');
        return;
      }

      await api.post(`/ordenes/${id}/enviar-cocina`);

      await api.post('/print-jobs', {
        tipo: 'comanda',
        referencia_id: parseInt(id),
        datos: { item_ids: pendingItems.map((i) => i.id) },
      });

      const updatedOrder = await api.get(`/ordenes/${id}`);
      setOrder(updatedOrder.data);

      Swal.fire({
        icon: 'success',
        title: '✅ Kitchen ticket sent',
        text: 'The ticket will print in the kitchen',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Could not print the kitchen ticket', 'error');
    } finally {
      sendingKitchenRef.current = false;
    }
  };

  const reprintAll = async () => {
    if (reprintingRef.current) return;
    reprintingRef.current = true;

    const sentItems = order.items.filter((item) => item.enviado_cocina);

    if (sentItems.length === 0) {
      Swal.fire('Notice', 'No items sent to the kitchen to reprint', 'warning');
      reprintingRef.current = false;
      return;
    }

    try {
      await api.post('/print-jobs', {
        tipo: 'comanda',
        referencia_id: parseInt(id),
        datos: { item_ids: sentItems.map((i) => i.id) },
      });

      Swal.fire({
        icon: 'success',
        title: '🖨️ Reprinting kitchen ticket',
        text: 'Reprinting the full kitchen ticket',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', 'Could not reprint the kitchen ticket', 'error');
    } finally {
      reprintingRef.current = false;
    }
  };

  const openChangeTable = async () => {
    try {
      const res = await api.get('/mesas');
      setAvailableTables(res.data.filter((m) => m.estado === 'disponible'));
      setShowChangeTableModal(true);
    } catch (error) {
      Swal.fire('Error', 'Could not load tables', 'error');
    }
  };

  const confirmChangeTable = async (targetTableId) => {
    setChangingTable(true);
    try {
      await api.put(`/ordenes/${id}/cambiar-mesa`, { mesa_destino_id: targetTableId });
      setShowChangeTableModal(false);
      await loadOrder();
      Swal.fire({
        icon: 'success',
        title: 'Table changed',
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Could not change tables', 'error');
    } finally {
      setChangingTable(false);
    }
  };

  const goToChargeSingleBill = () => {
    const items = order.items.map((item) => ({
      uniqueKey: `${item.product_id}-${item.id}`,
      product_id: item.product_id,
      presentation_id: null,
      codigo: item.producto?.codigo || `PROD-${item.product_id}`,
      nombre: item.producto?.nombre,
      precio_unitario: parseFloat(item.precio_unitario),
      cantidad: item.cantidad,
      subtotal: parseFloat(item.subtotal),
      is_taxable: Boolean(item.producto?.is_taxable),
    }));

    const total = items.reduce((acc, i) => acc + i.subtotal, 0);

    setItemsForCharge(items);
    setChargeTotal(total);
    setIsSplit(false);
    setShowBillTypeModal(false);
    setShowChargeModal(true);
  };

  const openSplitBill = () => {
    setShowBillTypeModal(false);
    setSelectedItems([]);
    setShowSplitModal(true);
  };

  const toggleItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((i) => i !== itemId) : [...prev, itemId]
    );
  };

  const pendingSplitItems =
    order?.items?.filter((item) => !itemsAlreadyInvoiced.includes(item.id)) || [];

  const goToChargeSplit = () => {
    if (selectedItems.length === 0) {
      Swal.fire('Error', 'Select at least one item', 'warning');
      return;
    }

    const items = order.items
      .filter((item) => selectedItems.includes(item.id))
      .map((item) => ({
        uniqueKey: `${item.product_id}-${item.id}`,
        product_id: item.product_id,
        presentation_id: null,
        codigo: item.producto?.codigo || `PROD-${item.product_id}`,
        nombre: item.producto?.nombre,
        precio_unitario: parseFloat(item.precio_unitario),
        cantidad: item.cantidad,
        subtotal: parseFloat(item.subtotal),
        is_taxable: Boolean(item.producto?.is_taxable),
      }));

    const total = items.reduce((acc, i) => acc + i.subtotal, 0);

    setItemsForCharge(items);
    setChargeTotal(total);
    setIsSplit(true);
    setShowSplitModal(false);
    setShowChargeModal(true);
  };

  const hasUnsentItems = order?.items?.some((item) => !item.enviado_cocina);

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.nombre.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'todas' || p.categoria === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `https://altivawebusa.com/BACK/public/storage/${photoPath}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-3" style={{ height: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <button
            className="btn btn-outline-secondary btn-sm me-2"
            onClick={() => navigate(order?.tipo === 'para_llevar' ? '/para-llevar' : '/mesas')}
          >
            ← Back
          </button>
          {order?.tipo === 'para_llevar' ? (
            <strong>
              📦 To Go #{order?.id}
              {order?.nombre_cliente && <span className="text-muted ms-2">— {order.nombre_cliente}</span>}
            </strong>
          ) : (
            <>
              <strong>Table {order?.mesa?.numero}</strong>
              {order?.descripcion && <span className="text-muted ms-2">— {order.descripcion}</span>}
              <span className="text-muted ms-2">— {order?.mesero?.nombre}</span>
            </>
          )}
        </div>
        <strong>Total: ${parseFloat(order?.total || 0).toFixed(2)}</strong>
      </div>

      {isMobile && (
        <div style={{ display: 'flex', marginBottom: '12px', borderBottom: '2px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveView('products')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              fontWeight: 600,
              background: activeView === 'products' ? '#667eea' : 'white',
              color: activeView === 'products' ? 'white' : '#4a5568',
              borderRadius: '8px 0 0 0',
            }}
          >
            🍽️ Products
          </button>
          <button
            onClick={() => setActiveView('order')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              fontWeight: 600,
              background: activeView === 'order' ? '#667eea' : 'white',
              color: activeView === 'order' ? 'white' : '#4a5568',
              borderRadius: '0 8px 0 0',
            }}
          >
            🧾 Order{' '}
            {order?.items?.length > 0 && (
              <span
                style={{
                  background: '#e53e3e',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '1px 6px',
                  fontSize: '11px',
                  marginLeft: '4px',
                }}
              >
                {order.items.length}
              </span>
            )}
          </button>
        </div>
      )}

      <div className={isMobile ? '' : 'row h-100'}>
        <div
          className={isMobile ? '' : 'col-7 border-end'}
          style={{ display: isMobile && activeView !== 'products' ? 'none' : 'block' }}
        >
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {[
              { value: 'todas', label: '🗂️ All' },
              { value: 'comida', label: '🍽️ Food' },
              { value: 'bebida', label: '🥤 Drinks' },
              { value: 'postre', label: '🍰 Desserts' },
              { value: 'otro', label: '📦 Other' },
            ].map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                style={{
                  padding: '5px 12px',
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: activeCategory === cat.value ? '#667eea' : '#e2e8f0',
                  background: activeCategory === cat.value ? '#667eea' : 'white',
                  color: activeCategory === cat.value ? 'white' : '#4a5568',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            className="form-control mb-3"
            placeholder="Search for a product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="row g-2" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {filteredProducts.map((product) => (
              <div key={product.id} className={isMobile ? 'col-6' : 'col-4'}>
                <div
                  className="card text-center"
                  style={{
                    cursor: adding[product.id] ? 'not-allowed' : 'pointer',
                    opacity: adding[product.id] ? 0.7 : 1,
                    transition: 'opacity 0.2s',
                    userSelect: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '10px',
                  }}
                  onClick={() => addProduct(product)}
                >
                  {product.foto ? (
                    <img
                      src={getPhotoUrl(product.foto)}
                      alt={product.nombre}
                      style={{ width: '100%', height: '90px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '90px',
                        background: '#f7fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                      }}
                    >
                      🍽️
                    </div>
                  )}
                  {adding[product.id] && (
                    <div style={{ position: 'absolute', top: 4, right: 6 }}>
                      <span className="spinner-border spinner-border-sm text-primary" style={{ width: 12, height: 12 }} />
                    </div>
                  )}
                  <div className="p-2">
                    <small className="fw-bold d-block" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                      {product.nombre}
                    </small>
                    {product.descripcion && (
                      <small className="text-muted d-block" style={{ fontSize: '10px', marginTop: '2px', lineHeight: '1.2' }}>
                        {product.descripcion}
                      </small>
                    )}
                    <small className="text-success fw-bold d-block" style={{ marginTop: '4px' }}>
                      ${parseFloat(product.precio_venta).toFixed(2)}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={isMobile ? '' : 'col-5'}
          style={{ display: isMobile && activeView !== 'order' ? 'none' : 'block' }}
        >
          <h6 className="mb-3">Current Order</h6>
          {!order?.items || order.items.length === 0 ? (
            <p className="text-muted text-center mt-4">No items added yet</p>
          ) : (
            <>
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {order.items.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <div>
                      <small className="fw-bold">{item.producto?.nombre}</small>
                      {item.listo ? (
                        <span className="badge bg-success ms-1" style={{ fontSize: '10px' }}>
                          ✓ Ready
                        </span>
                      ) : item.enviado_cocina ? (
                        <span className="badge bg-warning ms-1" style={{ fontSize: '10px' }}>
                          Kitchen
                        </span>
                      ) : null}
                      <br />
                      <small className="text-muted">
                        x{item.cantidad} — ${parseFloat(item.precio_unitario).toFixed(2)}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <small className="fw-bold">${parseFloat(item.subtotal).toFixed(2)}</small>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: '2px 6px', fontSize: '12px' }}
                        onClick={() => removeItem(item.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between mb-2">
                  <strong>Total:</strong>
                  <strong>${parseFloat(order?.total || 0).toFixed(2)}</strong>
                </div>
                {hasUnsentItems && (
                  <button className="btn btn-warning w-100 mt-2" onClick={sendToKitchen}>
                    🖨️ Print Kitchen Ticket
                  </button>
                )}
                {order?.items?.some((item) => item.enviado_cocina) && (
                  <button className="btn btn-outline-secondary w-100 mt-2" onClick={reprintAll}>
                    🔄 Reprint Full Kitchen Ticket
                  </button>
                )}
                {order?.tipo === 'mesa' && (
                  <button className="btn btn-outline-primary w-100 mt-2" onClick={openChangeTable}>
                    🔁 Change Table
                  </button>
                )}
                <button className="btn btn-success w-100 mt-2" onClick={() => setShowBillTypeModal(true)}>
                  Close Bill
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile && activeView === 'products' && order?.items?.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'white',
            borderTop: '2px solid #e2e8f0',
            padding: '10px 16px',
            zIndex: 1000,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
            {order.items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '13px',
                }}
              >
                <span>
                  <strong>x{item.cantidad}</strong> {item.producto?.nombre}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#38a169', fontWeight: 600 }}>${parseFloat(item.subtotal).toFixed(2)}</span>
                  <button
                    onClick={() => removeItem(item.id)}
                    style={{ background: 'none', border: 'none', color: '#e53e3e', fontSize: '14px', cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Total: ${parseFloat(order?.total || 0).toFixed(2)}</strong>
            <div style={{ display: 'flex', gap: '8px' }}>
              {hasUnsentItems && (
                <button className="btn btn-warning btn-sm" onClick={sendToKitchen}>
                  🖨️ Print
                </button>
              )}
              {order?.items?.some((item) => item.enviado_cocina) && (
                <button className="btn btn-outline-secondary btn-sm" onClick={reprintAll}>
                  🔄 Reprint
                </button>
              )}
              <button className="btn btn-success btn-sm" onClick={() => setShowBillTypeModal(true)}>
                Close Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {showBillTypeModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Close Bill</h5>
                <button className="btn-close" onClick={() => setShowBillTypeModal(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">How will this be paid?</p>
                <div className="d-flex flex-column gap-3">
                  <button className="btn btn-success btn-lg w-100" onClick={goToChargeSingleBill} disabled={isSubmitting}>
                    🧾 Single bill
                  </button>
                  <button className="btn btn-outline-primary btn-lg w-100" onClick={openSplitBill}>
                    👥 Split bill
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showChangeTableModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Table</h5>
                <button className="btn-close" onClick={() => setShowChangeTableModal(false)} />
              </div>
              <div className="modal-body">
                {availableTables.length === 0 ? (
                  <p className="text-muted text-center">No tables available</p>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {availableTables.map((table) => (
                      <button
                        key={table.id}
                        className="btn btn-outline-primary"
                        disabled={changingTable}
                        onClick={() => confirmChangeTable(table.id)}
                      >
                        Table {table.numero}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSplitModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Split Bill</h5>
                  <small className="text-muted">
                    Select this person's items
                    {itemsAlreadyInvoiced.length > 0 && (
                      <span className="badge bg-success ms-2">{itemsAlreadyInvoiced.length} already charged</span>
                    )}
                  </small>
                </div>
                <button className="btn-close" onClick={() => setShowSplitModal(false)} />
              </div>
              <div className="modal-body">
                {pendingSplitItems.length === 0 ? (
                  <div className="text-center text-success py-3">
                    <div style={{ fontSize: 40 }}>✅</div>
                    <p className="mt-2 fw-bold">All items have been charged</p>
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                      Check the items this person will pay for:
                    </p>
                    {pendingSplitItems.map((item) => (
                      <div
                        key={item.id}
                        className="d-flex align-items-center gap-3 border rounded p-2 mb-2"
                        style={{
                          cursor: 'pointer',
                          background: selectedItems.includes(item.id) ? '#f0fdf4' : 'white',
                          borderColor: selectedItems.includes(item.id) ? '#22c55e' : '#dee2e6',
                        }}
                        onClick={() => toggleItem(item.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                          style={{ width: 18, height: 18 }}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-bold" style={{ fontSize: 14 }}>
                            {item.producto?.nombre}
                          </div>
                          <small className="text-muted">x{item.cantidad}</small>
                        </div>
                        <div className="fw-bold text-success">${parseFloat(item.subtotal).toFixed(2)}</div>
                      </div>
                    ))}
                    {selectedItems.length > 0 && (
                      <div className="border-top pt-2 mt-2 d-flex justify-content-between fw-bold">
                        <span>Selected total:</span>
                        <span className="text-success">
                          $
                          {order.items
                            .filter((i) => selectedItems.includes(i.id))
                            .reduce((acc, i) => acc + parseFloat(i.subtotal), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {pendingSplitItems.length > 0 && (
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowSplitModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-success" onClick={goToChargeSplit} disabled={selectedItems.length === 0}>
                    Go to Checkout →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showChargeModal && (
        <ModalCobro
          items={itemsForCharge}
          total={chargeTotal}
          ordenId={id}
          esSeparada={isSplit}
          onSuccess={(data) => {
            setSaleResult(data);
            setShowChargeModal(false);
          }}
          onCancel={() => setShowChargeModal(false)}
        />
      )}

      {saleResult && (
        <Ticket
          invoice={saleResult}
          onAfterPrint={async () => {
            setSaleResult(null);
            if (!isSplit) {
              navigate('/mesas');
            } else {
              // Ask the server whether there are still pending items in the order
              // (the backend already removed the invoiced items and closed the order
              // automatically if nothing was left — see SaleController@store)
              const updatedOrder = await api.get(`/ordenes/${id}`);

              if (!updatedOrder.data.items || updatedOrder.data.items.length === 0) {
                navigate('/mesas');
              } else {
                setOrder(updatedOrder.data);
                setSelectedItems([]);
                setShowSplitModal(true);
              }
            }
          }}
        />
      )}
    </div>
  );
}